/**
 * Times on the user's clock, as moment formats. A leaf module (moment only)
 * so `dateRange.ts`, which `index.ts` imports, can use it without a cycle.
 */
import moment from 'moment-timezone';
import type {DateTimeConfig} from './index';

// Languages that write the meridiem before the hour: "오후 2:00", "午後 2:00".
const MERIDIEM_FIRST = ['ja', 'ko'];

/** Whether the active language writes the meridiem before the hour. */
export const isMeridiemFirst = () => MERIDIEM_FIRST.includes(moment.locale());

/**
 * The 12-hour clock format for the active language: `h:mm A` ("2:30 PM"),
 * or `h A` ("2 PM") when `minutes` is false. Japanese and Korean put the
 * meridiem first and keep the minutes, as in ko's own `LT`: "2 午後" reads
 * backwards (FE #428).
 */
export const twelveHourFormat = ({
  minutes = true,
}: {minutes?: boolean} = {}) => {
  if (isMeridiemFirst()) {
    return 'A h:mm';
  }
  return minutes ? 'h:mm A' : 'h A';
};

/**
 * A time on the user's clock: `HH:mm` ("20:49") or the 12-hour form
 * ("8:49 PM"), unpadded as the 12-hour clock is written, unlike
 * `hourFormatter`'s `hh:mm A` (FE #439). Without preferences, 24-hour, as
 * the chart formats always were (FE #428).
 */
export const clockFormat = (dateTimeConfig?: DateTimeConfig) =>
  dateTimeConfig && !dateTimeConfig.is_24_hour ? twelveHourFormat() : 'HH:mm';
